import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { fetchTagDefinitions } from "../../../api/tagDefinitions";
import { TWclassNames } from "../../utils/Div";

/** Normalize API / JSON tag ids so number vs string mismatches do not break selection. */
export function coerceCaseTagId(id) {
  if (id === null || id === undefined || id === "") return null;
  if (typeof id === "number" && Number.isFinite(id)) return id;
  const n = Number(id);
  if (Number.isFinite(n) && String(n) === String(id).trim()) return n;
  return id;
}

export function normalizeCaseTagIds(ids) {
  const arr = Array.isArray(ids) ? ids : [];
  return arr.map(coerceCaseTagId).filter((id) => id !== null && id !== undefined && id !== "");
}

function tagIdsMatch(a, b) {
  return String(coerceCaseTagId(a)) === String(coerceCaseTagId(b));
}

/**
 * Multi-select for case-level tag_definitions (labels like "Henvendelse via Google").
 * Scope: tag_definitions.scope_module (e.g. case, task).
 *
 * @param {"popover"|"inline"} [variant] — `inline` avoids clipped dropdowns inside overflow panels (e.g. guide builder sidebar).
 */
export function CaseTagSelector({
  value = [],
  onChange,
  readOnly,
  formik,
  name = "tag_ids",
  showBadges = true,
  scopeModule = "case",
  variant = "popover",
}) {
  const { t } = useTranslation();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const getSelectedRaw = () => (formik ? formik.values[name] || [] : value || []);

  const setSelectedIds = formik ? (ids) => formik.setFieldValue(name, ids) : onChange;

  useEffect(() => {
    fetchTagDefinitions(scopeModule)
      .then((data) => setTags(Array.isArray(data) ? data : []))
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  }, [scopeModule]);

  const toggleTag = (tag) => {
    const tid = coerceCaseTagId(tag.id);
    if (tid === null || tid === undefined || tid === "") return;
    const prev = normalizeCaseTagIds(getSelectedRaw());
    const has = prev.some((id) => tagIdsMatch(id, tid));
    const next = has ? prev.filter((id) => !tagIdsMatch(id, tid)) : [...prev, tid];
    setSelectedIds(next);
  };

  const removeTag = (e, tag) => {
    e.stopPropagation();
    const tid = coerceCaseTagId(tag.id);
    const prev = normalizeCaseTagIds(getSelectedRaw());
    setSelectedIds(prev.filter((id) => !tagIdsMatch(id, tid)));
  };

  const selectedNorm = normalizeCaseTagIds(getSelectedRaw());
  const selectedTags = tags.filter((tag) => selectedNorm.some((id) => tagIdsMatch(id, tag.id)));

  if (readOnly) {
    return (
      <div className="flex flex-wrap gap-1">
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className={TWclassNames(
              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
              tag.color ? "" : "bg-gray-100 text-gray-800"
            )}
            style={tag.color ? { backgroundColor: tag.color + "30", color: tag.color } : {}}
          >
            {tag.name}
          </span>
        ))}
      </div>
    );
  }

  const badges =
    showBadges && selectedTags.length > 0 ? (
      <div className="flex flex-wrap gap-1">
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            className={TWclassNames(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium",
              tag.color ? "" : "bg-gray-100 text-gray-800"
            )}
            style={tag.color ? { backgroundColor: tag.color + "30", color: tag.color } : {}}
          >
            {tag.name}
            <button
              type="button"
              onClick={(e) => removeTag(e, tag)}
              className="ml-0.5 hover:text-red-600 text-gray-500"
              aria-label={t("Remove")}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    ) : null;

  if (variant === "inline") {
    return (
      <div className="flex flex-col gap-2">
        <div className="max-h-52 overflow-y-auto rounded-md border border-gray-200 bg-white p-2">
          {loading ? (
            <div className="px-2 py-2 text-sm text-gray-500">{t("Loading...")}</div>
          ) : tags.length === 0 ? (
            <div className="px-2 py-2 text-sm text-gray-500">{t("No labels defined")}</div>
          ) : (
            <ul className="space-y-0.5">
              {tags.map((tag) => {
                const checked = selectedNorm.some((id) => tagIdsMatch(id, tag.id));
                return (
                  <li key={tag.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
                      <input type="checkbox" className="rounded border-gray-300 text-emerald-600" checked={checked} onChange={() => toggleTag(tag)} />
                      {tag.color ? (
                        <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                      ) : null}
                      <span className="text-gray-800">{tag.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {badges}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Popover className="relative">
        <Popover.Button className="inline-flex items-center gap-x-1 text-sm px-2 leading-6 text-blue-400 hover:text-blue-600">
          <span>{t("Labels")}</span>
          <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
        </Popover.Button>
        <Popover.Panel className="absolute left-0 z-[80] mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">{t("Loading...")}</div>
          ) : tags.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">{t("No labels defined")}</div>
          ) : (
            tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag)}
                className={TWclassNames(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                  selectedNorm.some((id) => tagIdsMatch(id, tag.id)) ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-700"
                )}
              >
                {tag.color && <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />}
                <span>{tag.name}</span>
              </button>
            ))
          )}
        </Popover.Panel>
      </Popover>
      {badges}
    </div>
  );
}
